/**
 * Semantic colours for candidate status and score bands.
 *
 * WHY THIS IS NOT PURELY DESIGN-SYSTEM TOKENS. The Avrio design system ships
 * one success green (#2BB656), one teal for status dots, and an accent capped
 * at roughly 5% of a surface. It has no palette for "this went badly" or "this
 * needs attention", because it was built for marketing surfaces where those
 * states do not arise.
 *
 * A recruiter scorecard has them. Rejected is not success, Not interested is
 * not a neutral tint, and mapping either onto the accent or the success green
 * would assert something false about the state. That gap is why
 * `#FFD016` sat as a bare literal in this page since tier 1, annotated as the
 * Candidate Scorecard tier's to settle.
 *
 * WHERE THESE COME FROM. The positive is the design system's own success
 * green. The caution and negative are the reference mockup's
 * (Avrio_UI_Reference_Pack/4_candidate_scorecard.html), which carries the
 * semantic set the system does not: green #1ca672, amber #c98a12, red #d64545,
 * each with a background tint. The mockup's brand colours were NOT taken --
 * its navy and purple predate the token layer -- but its semantic trio fills a
 * real hole and is Nachi's own reference.
 *
 * So: brand from the design system, semantics from the mockup, and this file
 * is the seam. If the design system later defines status colours, they win and
 * this file should shrink to re-exports.
 */

export const statusPalette = {
  /** Advanced, hired, matched. The design system's own success green. */
  positive: '#2BB656',
  positiveBg: '#E7F7EE',
  /** Not interested, withdrawn, needs attention. No design-system equivalent. */
  caution: '#C98A12',
  cautionBg: '#FDF3DC',
  /** Rejected. No design-system equivalent. */
  negative: '#D64545',
  negativeBg: '#FDEAEA',
  /** Neutral / not yet reached. */
  idle: '#9CA3AF',
  idleBg: '#EEEEF1',
} as const;

/**
 * Score bands for a 0-100 fit score.
 *
 * DELIBERATELY NOT A THRESHOLD. The reference mockup draws a "65 Fitscore
 * threshold" marker; there is no such thing any more. `candidates.min-fit-score`
 * was removed in avrio-backend#38 and selection is percentile per job per pool,
 * because a fixed bar surfaces nobody on a hard job -- job 3855 returned fifty
 * candidates whose best score was 56.
 *
 * These bands colour a number for legibility. They do not gate anything, and
 * nothing downstream should read them as if they did.
 */
export const scoreBand = (score: number): keyof typeof statusPalette => {
  if (!Number.isFinite(score)) return 'idle';
  if (score >= 75) return 'positive';
  if (score >= 50) return 'caution';
  return 'negative';
};

/**
 * A candidate status, grouped into the four things a recruiter scanning a list
 * actually needs to tell apart.
 *
 * WHAT THIS REPLACES. The candidates list coloured its status pill with:
 *
 *     if (status === 'ACTIVE')                            -> green
 *     else if (REJECTED || NOT_INTERESTED)                -> red
 *     else if (status === 'NOT_REVIEWED')                 -> no colour
 *     else                                                -> purple
 *
 * `ACTIVE` and `NOT_REVIEWED` are not in the Status enum and never have been,
 * so two of those four branches could not fire. The else swallowed seven states
 * — including HIRED, which rendered in the same lavender as a candidate nobody
 * had opened. The one unambiguously good outcome was indistinguishable from the
 * default, and the green branch meant to catch it was testing for a status that
 * does not exist.
 *
 * FOUR TONES, NOT NINE. Nine colours is not a legend anyone reads across 77
 * rows. These group by what the reader does next:
 *
 *   done      HIRED           — finished well
 *   stopped   REJECTED        — finished badly
 *   parked    NOT_INTERESTED  — finished, nobody's fault, may revisit
 *   active    invited through HM review — in flight, someone is waiting
 *   idle      NEW, REVIEWED   — not started
 *
 * `active` is deliberately ink rather than the accent. The brand caps the
 * accent near 5% of a surface, and a list of 77 rows where most are in flight
 * would be most of the page. Ink reads as "live" without spending it.
 */
export type StatusTone = 'done' | 'stopped' | 'parked' | 'active' | 'idle';

const TONE_BY_STATUS: Record<string, StatusTone> = {
  HIRED: 'done',
  REJECTED: 'stopped',
  NOT_INTERESTED: 'parked',
  INVITATION_SENT: 'active',
  RIO_SCREENING: 'active',
  ALT_INTERVIEW: 'active',
  HM_REVIEW: 'active',
  OFFERED: 'active',
  NEW: 'idle',
  REVIEWED: 'idle',
  OPEN: 'active',
};

/** Unknown statuses read as idle rather than as anything more definite. */
export const statusTone = (status?: string): StatusTone =>
  (status && TONE_BY_STATUS[status]) || 'idle';

export const toneStyle = (
  tone: StatusTone,
): { color: string; background: string; borderColor: string } => {
  switch (tone) {
    case 'done':
      return {
        color: statusPalette.positive,
        background: statusPalette.positiveBg,
        borderColor: 'transparent',
      };
    case 'stopped':
      return {
        color: statusPalette.negative,
        background: statusPalette.negativeBg,
        borderColor: 'transparent',
      };
    case 'parked':
      return {
        color: statusPalette.caution,
        background: statusPalette.cautionBg,
        borderColor: 'transparent',
      };
    case 'active':
      return {
        color: '#0D0F14',
        background: 'transparent',
        borderColor: '#E5E7EB',
      };
    default:
      return {
        color: statusPalette.idle,
        background: statusPalette.idleBg,
        borderColor: 'transparent',
      };
  }
};
